/**
 * Volume of Rectangular Prisms generator (6.MGSR.1.5).
 *
 * Volume of rectangular prisms, including prisms with a fractional edge length.
 * Volumes are computed with the exact-rational geometry helper, so a fractional
 * edge yields an exact fractional volume (e.g. 45/2), and display and answer
 * always agree.
 *
 * Families:
 *   F1: whole dimensions            (l w h)
 *   F2: one fractional edge         (l w h, h = a/2 or a/4)
 *   F3: cube                        (s^3)
 *   F4: unit-cube count             (count of 1x1x1 cubes that fill the prism)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { volumeRectPrism } from "../engine/geometry";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "volume-prisms" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F4"], max: 6 },
  standard: { families: ["F1", "F3", "F4"], max: 10 },
  hard: { families: ["F1", "F2", "F3"], max: 12 },
  challenge: { families: ["F2", "F3"], max: 15 },
};

export class VolumePrismsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let prompt: string;
      let latex: string;
      let answer: Rational;
      let stepDesc: string;

      if (family === "F1" || family === "F4") {
        const l = rng.int(2, cfg.max);
        const w = rng.int(2, cfg.max);
        const h = rng.int(2, cfg.max);
        answer = volumeRectPrism(rat(l), rat(w), rat(h));
        if (family === "F4") {
          prompt =
            `A right rectangular prism is ${l} units long, ${w} units wide, and ${h} units tall. ` +
            `How many unit cubes (1 by 1 by 1) are needed to completely fill it?`;
          stepDesc = "The number of unit cubes that fill a prism equals its volume: length times width times height.";
        } else {
          prompt = `Find the volume of a rectangular prism with length ${l}, width ${w}, and height ${h}.`;
          stepDesc = "Volume of a rectangular prism is length times width times height.";
        }
        latex = `V = l w h = ${l} \\times ${w} \\times ${h} = ${answer.toLatex()}`;
      } else if (family === "F2") {
        const l = rng.int(2, cfg.max);
        const w = rng.int(2, cfg.max);
        const den = rng.pick([2, 4]);
        const num = rng.int(1, den * cfg.max);
        const h = rat(num, den);
        answer = volumeRectPrism(rat(l), rat(w), h);
        prompt =
          `Find the volume of a rectangular prism with length ${l}, width ${w}, and height ${h.toString()}.`;
        latex = `V = l w h = ${l} \\times ${w} \\times ${h.toLatex()} = ${answer.toLatex()}`;
        stepDesc = "Multiply length, width, and height; a fractional edge gives a fractional volume.";
      } else {
        // F3: cube.
        const s = rng.int(2, cfg.max);
        answer = volumeRectPrism(rat(s), rat(s), rat(s));
        prompt = `Find the volume of a cube with edge length ${s}.`;
        latex = `V = s^3 = ${s}^3 = ${answer.toLatex()}`;
        stepDesc = "A cube's volume is the edge length cubed.";
      }

      if (!answer.isPositive()) continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: prompt },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [{ description: stepDesc, latex }],
        hints: [
          { level: 1, text: "Volume of a rectangular prism is length times width times height." },
          { level: 2, text: "Multiply all three edge lengths together, keeping fractions exact." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const volumePrisms = new VolumePrismsGenerator();
